/* ==============================================
   GOLD SKY — goldsky-cart.js
   Global cart system.
   Handles:
   - Cart drawer open/close
   - AJAX add to cart (all ATC buttons sitewide)
   - Cart count updates in nav
   - Free shipping progress bar
   - Cart drawer HTML refresh after every change
   - Quantity controls in drawer and cart page
   ============================================== */

(function () {
  'use strict';

  /* ── CONFIG ── */
  var FREE_SHIPPING_THRESHOLD = window.gsConfig && window.gsConfig.freeShippingThreshold
    ? window.gsConfig.freeShippingThreshold
    : 99900; /* paise — ₹999 default */

  /* ── SELECTORS ── */
  function el(id) { return document.getElementById(id); }

  var drawer     = el('gsCartDrawer');
  var overlay    = el('gsCartOverlay');
  var closeBtn   = el('gsCartDrawerClose');
  var drawerBody = el('gsDrawerBody');
  var drawerFooter = el('gsDrawerFooter');
  var drawerCount  = el('gsDrawerCount');
  var drawerTotal  = el('gsDrawerTotal');
  var drawerShipping = el('gsDrawerShipping');
  var navCount   = el('cartCount');
  var bagBtn     = el('gsBagBtn');

  /* ── CURRENCY FORMATTER ── */
  var formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0
  });

  function formatMoney(cents) {
    return formatter.format(cents / 100);
  }

  /* ── OPEN CART DRAWER ── */
  window.openCartDrawer = function () {
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) {
      overlay.classList.add('is-visible');
      overlay.setAttribute('aria-hidden', 'false');
    }
    document.body.style.overflow = 'hidden';
    gsRefreshDrawer();
  };

  /* ── CLOSE CART DRAWER ── */
  window.gsCloseCartDrawer = function () {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (overlay) {
      overlay.classList.remove('is-visible');
      overlay.setAttribute('aria-hidden', 'true');
    }
    document.body.style.overflow = '';
  };

  /* Close triggers */
  if (closeBtn) closeBtn.addEventListener('click', window.gsCloseCartDrawer);
  if (overlay) overlay.addEventListener('click', window.gsCloseCartDrawer);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') window.gsCloseCartDrawer();
  });

  /* ── UPDATE NAV COUNT ── */
  function updateNavCount(itemCount) {
    if (navCount) {
      navCount.textContent = itemCount;
      navCount.dataset.count = itemCount;
      if (itemCount > 0) {
        navCount.classList.remove('gs-nav__cart-count--hidden');
      } else {
        navCount.classList.add('gs-nav__cart-count--hidden');
      }
    }
    if (bagBtn) {
      bagBtn.textContent = 'Bag (' + itemCount + ')';
    }
    if (drawerCount) {
      drawerCount.textContent = itemCount;
    }
  }

  /* ── UPDATE SHIPPING BAR ── */
  function updateShippingBar(totalCents) {
    if (!drawerShipping) return;
    var pct = Math.min(Math.round((totalCents / FREE_SHIPPING_THRESHOLD) * 100), 100);
    var fill = drawerShipping.querySelector('.gs-drawer-shipping__fill');
    var text = drawerShipping.querySelector('.gs-drawer-shipping__text');
    if (fill) {
      fill.style.width = pct + '%';
      if (pct >= 100) {
        fill.classList.add('gs-drawer-shipping__fill--done');
      } else {
        fill.classList.remove('gs-drawer-shipping__fill--done');
      }
    }
    if (text) {
      if (pct >= 100) {
        text.innerHTML = '<strong>Free delivery unlocked!</strong>';
      } else {
        var remaining = formatMoney(FREE_SHIPPING_THRESHOLD - totalCents);
        text.innerHTML = 'Add <strong>' + remaining + '</strong> more for free delivery';
      }
    }
  }

  /* ── REFRESH DRAWER HTML ── */
  function gsRefreshDrawer() {
    fetch('/?section_id=gs-cart-drawer', {
      headers: { 'Accept': 'text/html' }
    })
    .then(function (r) { return r.text(); })
    .then(function (html) {
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');

      var newBody    = doc.getElementById('gsDrawerBody');
      var newFooter  = doc.getElementById('gsDrawerFooter');
      var newCount   = doc.getElementById('gsDrawerCount');
      var newTotal   = doc.getElementById('gsDrawerTotal');
      var newShip    = doc.getElementById('gsDrawerShipping');

      if (drawerBody && newBody) drawerBody.innerHTML = newBody.innerHTML;
      if (drawerFooter) {
        if (newFooter) {
          drawerFooter.innerHTML = newFooter.innerHTML;
        } else {
          drawerFooter.innerHTML = '';
        }
      }
      if (drawerCount && newCount) drawerCount.textContent = newCount.textContent;
      if (drawerTotal && newTotal) drawerTotal.textContent = newTotal.textContent;
      if (drawerShipping && newShip) drawerShipping.innerHTML = newShip.innerHTML;

      /* Re-bind events after DOM update */
      gsBindDrawerEvents();
    })
    .catch(function (err) {
      console.error('[GS Cart] Drawer refresh failed:', err);
    });
  }

  /* ── AJAX ADD TO CART ── */
  window.gsAddToCart = function (variantId, quantity, btn) {
    quantity = quantity || 1;

    if (btn) {
      var original = btn.textContent;
      btn.textContent = 'Adding…';
      btn.disabled = true;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ id: variantId, quantity: quantity })
    })
    .then(function (r) { return r.json(); })
    .then(function () {
      return fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });
    })
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      updateNavCount(cart.item_count);
      updateShippingBar(cart.total_price);

      if (btn) {
        btn.textContent = '✓ Added!';
        setTimeout(function () {
          btn.textContent = original;
          btn.disabled = false;
        }, 2000);
      }

      window.openCartDrawer();
    })
    .catch(function (err) {
      console.error('[GS Cart] Add to cart failed:', err);
      if (btn) {
        btn.textContent = original || 'Add to Bag';
        btn.disabled = false;
      }
    });
  };

  /* ── AJAX CHANGE CART ITEM ── */
  function gsChangeCart(key, qty, onSuccess) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ id: key, quantity: qty })
    })
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      updateNavCount(cart.item_count);
      updateShippingBar(cart.total_price);
      if (typeof onSuccess === 'function') onSuccess(cart);
      gsRefreshDrawer();
    })
    .catch(function (err) {
      console.error('[GS Cart] Cart change failed:', err);
    });
  }

  /* ── BIND DRAWER EVENTS ── */
  function gsBindDrawerEvents() {
    if (!drawerBody) return;

    /* Qty buttons inside drawer */
    drawerBody.querySelectorAll('.gs-qty__btn[data-action]').forEach(function (btn) {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', function () {
        var key = btn.dataset.key;
        var input = drawerBody.querySelector('.gs-qty__num[data-key="' + key + '"]');
        if (!input) return;
        var current = parseInt(input.value, 10);
        var newQty = btn.dataset.action === 'increase'
          ? current + 1
          : Math.max(0, current - 1);
        input.value = newQty;
        gsChangeCart(key, newQty);
      });
    });

    /* Remove buttons inside drawer */
    drawerBody.querySelectorAll('.gs-drawer-item__remove').forEach(function (btn) {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', function () {
        gsChangeCart(btn.dataset.key, 0);
      });
    });
  }

  /* ── BIND PDP ATC BUTTON ── */
  function bindPdpAtc() {
    var atcBtn    = el('gsPdpAtcBtn');
    var mobAtcBtn = el('gsMobAtcBtn');
    var qtyInput  = el('gsPdpQtyNum');

    function handlePdpAtc(btn) {
      if (!btn || btn.disabled) return;
      var variantId = btn.dataset.variantId;
      var qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      window.gsAddToCart(variantId, qty, btn);
    }

    if (atcBtn) {
      atcBtn.addEventListener('click', function () { handlePdpAtc(atcBtn); });
    }
    if (mobAtcBtn) {
      mobAtcBtn.addEventListener('click', function () { handlePdpAtc(mobAtcBtn); });
    }
  }

  /* ── BIND QUICK ADD BUTTONS (product cards) ── */
  function bindQuickAdd() {
    document.querySelectorAll('.gs-pcard__quick-add:not(.gs-pcard__quick-add--sold-out)').forEach(function (btn) {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var variantId = btn.dataset.variantId;
        if (!variantId) {
          window.location.href = btn.dataset.productUrl;
          return;
        }
        window.gsAddToCart(variantId, 1, btn);
      });
    });
  }

  /* ── BIND CART PAGE CONTROLS ── */
  function bindCartPage() {
    /* Qty buttons on cart page */
    document.querySelectorAll('#gsCartItems .gs-qty__btn[data-action]').forEach(function (btn) {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', function () {
        var key = btn.dataset.key;
        var input = document.querySelector('#gsCartItems .gs-qty__num[data-key="' + key + '"]');
        if (!input) return;
        var current = parseInt(input.value, 10);
        var newQty = btn.dataset.action === 'increase'
          ? current + 1
          : Math.max(0, current - 1);
        input.value = newQty;
        gsCartPageChange(key, newQty);
      });
    });

    /* Remove buttons on cart page */
    document.querySelectorAll('#gsCartItems .gs-cart-item__remove').forEach(function (btn) {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', function () {
        gsCartPageChange(btn.dataset.key, 0);
      });
    });

    /* Upsell add buttons on cart page */
    document.querySelectorAll('.gs-cart-upsell-card__btn').forEach(function (btn) {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', function () {
        var original = btn.textContent;
        btn.textContent = '…';
        btn.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: btn.dataset.variantId, quantity: 1 })
        })
        .then(function (r) { return r.json(); })
        .then(function () {
          btn.textContent = '✓ Added';
          setTimeout(function () { window.location.reload(); }, 800);
        })
        .catch(function () {
          btn.textContent = original;
          btn.disabled = false;
        });
      });
    });
  }

  /* Cart page AJAX change — updates totals without full reload */
  function gsCartPageChange(key, qty) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      /* Remove item row if qty is 0 */
      if (qty === 0) {
        var itemEl = document.querySelector('.gs-cart-item[data-key="' + key + '"]');
        if (itemEl) itemEl.remove();
      }

      /* Update totals */
      var totalStr = formatMoney(cart.total_price);
      var subEl = el('gsCartSubtotal');
      var totEl = el('gsCartTotal');
      if (subEl) subEl.textContent = totalStr;
      if (totEl) totEl.textContent = totalStr;

      /* Update nav */
      updateNavCount(cart.item_count);

      /* Reload if empty */
      if (cart.item_count === 0) {
        window.location.reload();
      }
    })
    .catch(function (err) {
      console.error('[GS Cart] Cart page change failed:', err);
    });
  }

  /* ── BIND BAG / CART ICON BUTTONS ── */
  function bindCartTriggers() {
    var cartBtn = el('gsCartBtn');
    var bagBtnEl = el('gsBagBtn');

    function handleCartTrigger(e) {
      e.preventDefault();
      window.openCartDrawer();
    }

    if (cartBtn) cartBtn.addEventListener('click', handleCartTrigger);
    if (bagBtnEl) bagBtnEl.addEventListener('click', handleCartTrigger);
  }

  /* ── INIT ── */
  function init() {
    gsBindDrawerEvents();
    bindPdpAtc();
    bindQuickAdd();
    bindCartPage();
    bindCartTriggers();
  }

  /* Run on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();